import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PageTitle from '../components/PageTitle';
import queryString from 'query-string';

import PasswordExpired from '../components/PasswordExpiredNotice';
import { URLParameters } from '../types';
import messages from './ExpiredPasswordPage.messages';
import TallButton from '../components/TallButton';
import ButtonContainer from '../components/ButtonContainer';
import { requestResetExpiredPassword } from '../actions/expired-password-actions';
import { addFlag } from '../actions/flag-actions';
import WhiteboxPage from '../components/WhiteboxPage';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { usePrevious } from '../utilities/hooks';
import { fetchPageState } from '../utilities/fetch-api';

import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import Heading from '@atlaskit/heading';

const pageId = 'expiredpassword';

export async function resetExpiredPasswordLoader({ request }: { request: Request }) {
  return await fetchPageState('/rest/login/resetexpiredpassword/state', request, {
    capabilities: ['capability-group--manage-profile', 'capability-name--reset-password'],
  });
}

export const ExpiredPasswordPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();

  const email = useAppSelector(state => state.user.email);
  const { error, isLoading, isComplete } = useAppSelector(state => state.expiredPassword);

  const dispatchResetPassword = useCallback(() => {
    const { token } = queryString.parse(location.search) as URLParameters;
    dispatch(requestResetExpiredPassword(token || ''));
  }, [dispatch, location.search]);

  const previousIsComplete = usePrevious(isComplete);

  usePageViewedEvent(pageId, {
    redirectReason: 'adminForcedReset',
  });

  useEffect(() => {
    if (!previousIsComplete && isComplete) {
      switch (error) {
        case null:
          dispatch(
            addFlag(
              'success',
              messages.emailSentFlagSuccessTitle,
              messages.emailSentFlagSuccessDesc
            )
          );
          break;

        case 'invalid_token':
          navigate(`/login?email=${encodeURIComponent(email)}`);
          dispatch(
            addFlag('error', messages.emailSentFlagFailedTitle, messages.emailSentFlagFailedDesc)
          );
          break;

        case 'too_many_requests':
          dispatch(
            addFlag(
              'error',
              messages.emailSentFlagFailedTitle,
              messages.emailSentFlagTooManyEmailsDesc
            )
          );
          break;

        default:
          dispatch(
            addFlag('error', messages.emailSentFlagFailedTitle, messages.emailSentFlagFailedDesc)
          );
          break;
      }
    }
  }, [dispatch, email, error, isComplete, previousIsComplete, navigate]);

  const headerMessage = formatMessage(messages.title);
  return (
    <WhiteboxPage
      // @ts-ignore no id
      id="expired-password-page"
      pageId={pageId}
      header={
        <Heading as="h1" size="small">
          {headerMessage}
        </Heading>
      }>
      <PageTitle title={formatMessage(messages.headTitle)} />
      <PasswordExpired
        header={formatMessage(messages.headTitle)}
        message={formatMessage(messages.contentText)}
        email={email}
      />
      <ButtonContainer>
        <TallButton
          appearance="primary"
          id="send-password-reset-email"
          type="submit"
          isFullWidth
          isLoading={isLoading}
          onClick={() => {
            dispatchResetPassword();
            analyticsClient.buttonClickedEvent(pageId, 'resetExpiredPasswordButton');
          }}>
          {formatMessage(messages.sendResetEmailButton)}
        </TallButton>
      </ButtonContainer>
    </WhiteboxPage>
  );
};

export default ExpiredPasswordPage;
